import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Swal, { SweetAlertResult } from 'sweetalert2';

import Allergen from 'classes/StandingData/Allergen/Search/Allergen';
import AllergenList from 'classes/StandingData/Allergen/Search/AllergenList';
import Search from 'classes/StandingData/Allergen/Search/Search';
import AllergenDetailComponent from 'components/desktop/StandingData/Allergen/Detail/AllergenDetailComponent';
import BtnGroup, { BtnData } from 'components/desktop/_general/Button/BtnGroup/BtnGroup';
import BtnNew from 'components/desktop/_general/Button/BtnNew/BtnNew';
import LoadingAnimation from 'components/desktop/_general/Loading/LoadingAnimation';
import Pagination from 'components/desktop/_general/Pagination/Pagination';
import AdvancedSearchInput from 'components/desktop/_general/Search/AdvancedSearchInput';
import StandingDataList, {
	Row,
} from 'components/desktop/_general/StandingDataList/StandingDataList';
import SvgBig from 'components/desktop/_general/SvgBig/SvgBig';
import clone from 'functions/clone';
import { QueryParams, getQueryParams } from 'functions/getQueryParams';
import useAirNavigation from 'hooks/useAirNavigation';
import useEffectWhenMounted from 'hooks/useEffectWhenMounted';
import { ISelectItem } from 'interfaces/ISelectItem';
import { RootState } from 'reducers/rootReducer';

interface IProps {
	archive?: boolean;
}

const maxNumberOfButtons = 5;

const AllergenManagementComponent = (props: IProps) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const reduxCultureCode: string = useSelector((state: RootState) => state.cultureCode);

	const [isLoading, setIsLoading] = useState<boolean>(false);
	const { params } = useAirNavigation();
	const displayOverlay = params.get('id') ? true : false;

	const [allergenList, setAllergenList] = useState<AllergenList>(new AllergenList());
	const [search, setSearch] = useState<Search>(new Search());
	const [searchTerm, setSearchTerm] = useState<string>(search.searchTerm.value ?? '');

	useEffect(() => {
		initialize();
	}, []);

	useEffectWhenMounted(() => {
		initialize();
	}, [reduxCultureCode]);

	const initialize = async (): Promise<void> => {
		if (props.archive !== undefined && props.archive == true) {
			search.showOnlyDeleted = true;
		}
		if (window.location.href.includes('?')) {
			const queryParams: QueryParams = getQueryParams();
			search.mapFromUrl(queryParams);
			await getAllergens();
		} else {
			await getAllergens();
		}
		setSearch(clone(search));
	};

	const getAllergens = async () => {
		setIsLoading(true);
		try {
			const response = await allergenList.callApi(search.mapToRequest());
			if (response.hasValue()) {
				allergenList.mapFromApi(response.get());
				setAllergenList(clone(allergenList));
			}
		} finally {
			setIsLoading(false);
		}
	};

	const getSuggestedAllergens = async (): Promise<ISelectItem[]> => {
		if (searchTerm.length >= 1) {
			await allergenList.search(searchTerm, props.archive ?? false);
		} else {
			allergenList.filtered = [];
		}
		setAllergenList(clone(allergenList));
		return allergenList.filtered;
	};

	const handleChangeUrl = () => {
		const url: string | null = search.mapToUrl();
		if (url) navigate(url);
		getAllergens();
	};

	const handleSearch = (input?: string) => {
		search.setSearchTerm(input ?? searchTerm);
		handleChangeUrl();
	};

	const handleChangePageIndex = (index: number) => {
		search.pageIndex.value = index;
		handleChangeUrl();
	};

	const handleChangeId = (id: string | null) => {
		search.id.value = id;
		handleChangeUrl();
	};

	const handleOverlayOpen = (input: string): void => {
		handleChangeId(input);
	};

	const handleOverlayClose = (): void => {
		handleChangeId(null);
	};

	const handleRestoreAllergen = async (allergen: Allergen): Promise<void> => {
		const result: SweetAlertResult = await Swal.fire({
			title: t('standingData:RESTORE_TITLE'),
			text: t('standingData:ALLERGEN_RESTORE_DIALOG'),
			icon: 'warning',
			showCancelButton: true,
			iconColor: '#00cc07',
			confirmButtonColor: '#00cc07',
			confirmButtonText: t('_general:RESTORE'),
			cancelButtonText: t('_general:CANCEL'),
		});
		if (result.value) {
			setIsLoading(true);
			try {
				await allergen.callApiRestore();
				await Swal.fire({
					title: t('standingData:ALLERGEN_RESTORE_CONFIRMATION'),
					confirmButtonColor: '#00cc07',
				});
				await getAllergens();
			} finally {
				setIsLoading(false);
			}
		}
	};

	const handleArchiveAllergen = async (allergen: Allergen): Promise<void> => {
		const result: SweetAlertResult = await Swal.fire({
			title: t('standingData:ARCHIVE_TITLE'),
			text: t('standingData:ALLERGEN_ARCHIVE_DIALOG'),
			icon: 'warning',
			showCancelButton: true,
			iconColor: '#ff0004',
			confirmButtonColor: '#ff0004',
			confirmButtonText: t('_general:ARCHIVE'),
			cancelButtonText: t('_general:CANCEL'),
		});
		if (result.value) {
			setIsLoading(true);
			try {
				await allergen.callApiDelete();
				await Swal.fire({
					title: t('standingData:ALLERGEN_ARCHIVE_CONFIRMATION'),
					confirmButtonColor: '#00cc07',
				});
				await getAllergens();
			} finally {
				setIsLoading(false);
			}
		}
	};

	const handleChangeIdToNew = (): void => {
		handleChangeId('new');
	};

	const getYesOrNo = (input: boolean): string => {
		if (input) {
			return t('_general:YES');
		} else {
			return t('_general:NO');
		}
	};

	const getButtonDataArchive = (input: Allergen): BtnData | undefined => {
		if (!props.archive) {
			return {
				onClick: () => handleArchiveAllergen(input),
			};
		}
	};

	const getButtonDataEdit = (id: string): BtnData | undefined => {
		if (!props.archive) {
			return {
				onClick: () => handleOverlayOpen(id),
			};
		}
	};

	const getButtonDataRestore = (input: Allergen): BtnData | undefined => {
		if (props.archive) {
			return {
				onClick: () => handleRestoreAllergen(input),
			};
		}
	};

	const renderButtons = (input: Allergen): JSX.Element => {
		return (
			<BtnGroup
				edit={getButtonDataEdit(input.id)}
				archive={getButtonDataArchive(input)}
				restore={getButtonDataRestore(input)}
			/>
		);
	};

	const renderNames = (allergen: Allergen): string => {
		const renderName = (input: string | null): string => {
			if (input) {
				return input;
			} else {
				return t('_general:UNKNOWN');
			}
		};
		return [
			renderName(allergen.positiveName),
			renderName(allergen.negativeName),
			renderName(allergen.mayContainName),
		].join(' / ');
	};

	const renderListRows = (): Row[] => {
		return allergenList.all.map((allergen: Allergen) => {
			return {
				id: allergen.id,
				jsx: (
					<div
						className="d-flex flex-row justify-content-start align-items-center"
						style={{ gap: '2em' }}
					>
						<div className="d-flex flex-row" style={{ gap: '0.8em', width: '200px' }}>
							<SvgBig url={allergen.positiveIconSvgUrl} width={56} height={56} />
							<SvgBig url={allergen.negativeIconSvgUrl} width={56} height={56} />
							<SvgBig url={allergen.mayContainIconSvgUrl} width={56} height={56} />
						</div>
						<div style={{ width: '450px' }}>{renderNames(allergen)}</div>
						<div className="flex-fill">
							<span className="grey">{t('standingData:DISPLAY')}: </span>
							{getYesOrNo(allergen.display)}
						</div>
						<div>{renderButtons(allergen)}</div>
					</div>
				),
			};
		});
	};

	const renderContent = (): JSX.Element => {
		return (
			<>
				<main className="container">
					<div className="d-flex flex-row justify-content-between">
						{!props.archive ? (
							<>
								<h1>{t('standingData:ALLERGEN_MANAGEMENT')}</h1>
								<div className="d-flex flex-gap-25">
									<BtnNew
										onClick={handleChangeIdToNew}
										name={t('standingData:CREATE_ALLERGEN')}
									/>
								</div>
							</>
						) : (
							<h1>{t('standingData:ALLERGEN_ARCHIVE')}</h1>
						)}
					</div>
					<br />
					<div className="row">
						<div className="col-lg-12">
							<div className="input-group mb-3">
								<AdvancedSearchInput
									debounceMilliSeconds={350}
									getSuggestedItems={getSuggestedAllergens}
									handleSearch={handleSearch}
									searchTerm={searchTerm}
									setSearchTerm={setSearchTerm}
									triggerSearchLetterAmount={1}
								/>
							</div>
						</div>
					</div>
					<br />
					<LoadingAnimation isLoading={isLoading} />
					<p>{`${allergenList.totalCount} ${t('_general:ALLERGENS')}`}</p>
					<StandingDataList
						displayOverlay={displayOverlay}
						rows={renderListRows()}
						overlay={<AllergenDetailComponent />}
						handleOverlayOpen={handleOverlayOpen}
						handleOverlayClose={handleOverlayClose}
					/>
					<br />
					<Pagination
						countOfAllListItems={allergenList.totalCount}
						pageIndex={search.getPageIndex()}
						setPageIndex={handleChangePageIndex}
						listItemAmount={search.getPageSize()}
						maxNumberOfButtons={maxNumberOfButtons}
					/>
				</main>
			</>
		);
	};

	return <>{renderContent()}</>;
};

export default AllergenManagementComponent;
